/**
 * Shared date / time formatters — every panel in the app should use
 * these so the formats stay consistent project-wide.
 *
 * Format spec (locked in 2026-05, refreshed 2026-06):
 *   - Date:      DD/MM/YYYY        (e.g. 10/05/2026)
 *   - Time:      HH:MM AM/PM       (e.g. 04:18 PM)
 *   - Combined:  DD/MM/YYYY HH:MM AM/PM   (single space, NO comma, NO seconds)
 *
 * British-English month / day ordering. Uppercase AM/PM (post-processed
 * because Intl uses lowercase in en-GB). Two-digit components throughout
 * so the columns line up in tables.
 *
 * Timezone: all formatters render in the ACTIVE DISPLAY TIMEZONE (see
 * `setDisplayTimeZone`), not the viewer's browser zone. This keeps a
 * multi-country SaaS consistent — a tenant in Mumbai and a colleague
 * travelling in London both see the same wall-clock for a task due date.
 * When the display zone is unset (Super Admins, who span tenants, and
 * before a tenant session loads) it falls back to the browser's zone.
 */

/**
 * Active display timezone (IANA id, e.g. 'Asia/Kolkata') applied to every
 * formatter below. `undefined` → browser-local fallback. Set once from
 * the tenant's configured zone in the auth store; a module-level variable
 * (rather than a per-call argument) keeps the ~hundreds of existing call
 * sites untouched.
 */
import { DateTime } from 'luxon'

// The COMPANY display zone (set by the auth store from the tenant) and the
// person's own zone + formats from My Preferences → Region & formats (set by
// display-prefs.js). The person's zone, when set, wins over the company's.
let displayTimeZone
let personalTimeZone
let formatPrefs = { dateFormat: 'dmy', timeFormat: '12', numberFormat: 'comma', weekStart: 'mon' }

/** The zone every formatter renders in: the person's own zone, else the
 *  company's, else the browser's (undefined). */
function activeZone() {
  return personalTimeZone || displayTimeZone
}

/**
 * The zone CALENDAR DAYS are read in: the company's (else the person's).
 *
 * Two kinds of value look alike but are not:
 *   - a moment (an activity, a timed deadline) — written in the PERSON's zone
 *     by the time-bearing helpers (formatTime, formatDateTime, formatPattern);
 *   - a calendar day (a deadline with no time — stored as 23:59 on the
 *     COMPANY's day — a daily report day, "due today" / "overdue") — read in
 *     the company's zone by the day-level helpers (formatDate,
 *     formatDateOnly, dayKey, todayKey, dayOffsetFromToday, startOfWeek), so
 *     "due 26 Sept" reads 26 Sept for everyone, wherever they are.
 * Date and number FORMATS always follow the person.
 */
function calendarZone() {
  return displayTimeZone || personalTimeZone
}

/** Set (or clear with null / '') the active display timezone. Called from
 *  the auth store whenever the tenant session changes. */
export function setDisplayTimeZone(tz) {
  const next = typeof tz === 'string' ? tz.trim() : tz
  if (!next) {
    displayTimeZone = undefined
    return
  }
  displayTimeZone = DateTime.local().setZone(next).isValid ? next : undefined
}

/**
 * Apply the person's region & format preferences (My Preferences). Called by
 * display-prefs.js whenever the effective set changes — including a live
 * preview — so every formatter below follows them. `timezone: 'company'` (or
 * anything invalid) falls back to the company zone.
 */
export function setPersonalFormats({ timezone, dateFormat, timeFormat, numberFormat, weekStart } = {}) {
  personalTimeZone =
    typeof timezone === 'string' && timezone !== 'company' && DateTime.local().setZone(timezone).isValid
      ? timezone
      : undefined
  formatPrefs = {
    dateFormat: DATE_PATTERNS[dateFormat] ? dateFormat : 'dmy',
    timeFormat: TIME_PATTERNS[timeFormat] ? timeFormat : '12',
    numberFormat: NUMBER_SEPARATORS[numberFormat] ? numberFormat : 'comma',
    weekStart: weekStart === 'sun' ? 'sun' : 'mon',
  }
}

/** Read the active display timezone (the person's, else the company's). */
export function getDisplayTimeZone() {
  return activeZone()
}

/** The company's zone, regardless of the person's own (e.g. to label it). */
export function getCompanyTimeZone() {
  return displayTimeZone
}

/* ── Format patterns (Luxon tokens) ─────────────────────────────────────── */

const DATE_PATTERNS = {
  dmy: 'dd/MM/yyyy',
  mdy: 'MM/dd/yyyy',
  iso: 'yyyy-MM-dd',
  long: 'd MMM yyyy',
}
const TIME_PATTERNS = { 12: 'hh:mm a', 24: 'HH:mm' }

/** The person's date pattern (Luxon tokens) — for bespoke shapes that still
 *  need to follow the date-format preference. */
export function datePattern() {
  return DATE_PATTERNS[formatPrefs.dateFormat]
}

/** The person's time pattern: "hh:mm a" (12-hour) or "HH:mm" (24-hour). */
export function timePattern() {
  return TIME_PATTERNS[formatPrefs.timeFormat]
}

/** True when the person reads a 24-hour clock. */
export function uses24HourClock() {
  return formatPrefs.timeFormat === '24'
}

/** Luxon weekday the person's week starts on: 1 = Monday, 7 = Sunday. For
 *  calendars, date pickers and personal views — shared reports use the
 *  company working week instead. */
export function weekStartWeekday() {
  return formatPrefs.weekStart === 'sun' ? 7 : 1
}

/** JS `getDay()` index the week starts on: 1 = Monday, 0 = Sunday. */
export function weekStartJsDay() {
  return formatPrefs.weekStart === 'sun' ? 0 : 1
}

/**
 * Inject the active display timezone into an Intl / `toLocale*` options
 * object. Use this in bespoke formatters that can't call the shared
 * formatDate/formatTime helpers because they need a different shape
 * (e.g. a custom "Mon 17 Jun" or "04:18 PM" format for an instant).
 *
 * ONLY use it for INSTANTS (a real moment in time — a timestamp, a
 * due-datetime, an activity time). Do NOT use it for calendar-grid date
 * math (month grids, weekday headers) or date-only values, where shifting
 * by an offset would move the day. Pass-through when no zone is set.
 */
export function zoned(opts) {
  const zone = activeZone()
  return zone ? { ...opts, timeZone: zone } : opts
}

function toDate(input) {
  if (input == null || input === '') return null
  const d = input instanceof Date ? input : new Date(input)
  if (Number.isNaN(d.getTime())) return null
  return d
}

/** Parse any accepted input into a Luxon DateTime in the active display
 *  timezone (browser-local when unset).
 *
 *  Date-only strings ("YYYY-MM-DD" — e.g. the Task Monitor feed's day
 *  keys, daily-report bucket dates) are CALENDAR DATES, not instants:
 *  they parse as midnight IN THE DISPLAY ZONE so they always render as
 *  the literal day they name. The old path (`new Date("YYYY-MM-DD")` =
 *  UTC midnight, then shifted into the display zone) invented a
 *  phantom time-of-day (05:30 AM for IST) and, for viewers west of
 *  UTC, rolled the date back a whole day (QA-reported as the same task
 *  showing different due dates on Dashboard vs Task Monitor). Full ISO
 *  datetimes / Date objects / epoch values are real instants and keep
 *  the existing zone-shifting behaviour. */
function toDT(input, zone = activeZone()) {
  if (typeof input === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(input)) {
    const dt = DateTime.fromISO(input, {
      zone: zone || 'local',
      locale: 'en-GB',
    })
    return dt.isValid ? dt : null
  }
  const d = toDate(input)
  if (!d) return null
  return DateTime.fromJSDate(d, {
    zone: zone || undefined,
    locale: 'en-GB',
  })
}

/** `toDT` for calendar-day helpers — read in the company's zone. */
const toDay = (input) => toDT(input, calendarZone())

/** The zone calendar days are read in (see `calendarZone`). For date-only
 *  pickers and day grids that must agree with `dayKey` / `formatDate`. */
export function getCalendarTimeZone() {
  return calendarZone()
}

/** Uppercase the am/pm meridiem to match the locked spec (en-GB can emit
 *  lowercase). */
function upperMeridiem(s) {
  return s.replace(/\b(am|pm)\b/i, (m) => m.toUpperCase())
}

/** Currency display symbol for the codes the platform sells in —
 *  matches the maps PlansPage / CouponField carry (user-facing money
 *  renders with the SYMBOL app-wide; only the invoice PDF uses ISO
 *  codes, for font reasons). Unknown codes fall back to "CODE ". */
const CURRENCY_SYMBOLS = { USD: '$', EUR: '€', GBP: '£', INR: '₹', CAD: 'CA$', AUD: 'A$' }
export function currencySymbol(code) {
  return CURRENCY_SYMBOLS[code] ?? (code ? `${code} ` : '')
}

/** Date only, in the person's date format (default "DD/MM/YYYY"). Returns
 *  '—' for missing / invalid values. */
export function formatDate(input) {
  const dt = toDay(input)
  return dt ? dt.toFormat(datePattern()) : '—'
}

/** Time only, in the person's clock (default "HH:MM AM/PM", or 24-hour). */
export function formatTime(input) {
  const dt = toDT(input)
  if (!dt) return '—'
  return upperMeridiem(dt.toFormat(timePattern()))
}

/**
 * Any bespoke shape (Luxon tokens, e.g. "EEE d MMM") for an INSTANT, rendered
 * in the person's zone. Use `datePattern()` / `timePattern()` inside the
 * pattern where the shape should still follow their date / clock choice.
 * Returns '' for missing / invalid input.
 */
export function formatPattern(input, pattern) {
  const dt = toDT(input)
  return dt ? upperMeridiem(dt.toFormat(pattern)) : ''
}

/** Start (00:00) of the week containing `input` in the person's zone, using
 *  their first day of the week. A Luxon DateTime, or null. */
export function startOfWeek(input = new Date()) {
  const dt = toDay(input)
  if (!dt) return null
  const back = (dt.weekday - weekStartWeekday() + 7) % 7
  return dt.minus({ days: back }).startOf('day')
}

/* ── Numbers ─────────────────────────────────────────────────────────────
 * The person's number format sets the thousands / decimal separators only.
 * A currency symbol comes from the money itself (`formatMoney`), never from
 * the format — "48 500,00 GBP" from a French locale reads as a fault. */

const NUMBER_SEPARATORS = {
  comma: { group: ',', decimal: '.' },
  dot: { group: '.', decimal: ',' },
  space: { group: ' ', decimal: ',' }, // no-break space keeps 1 234 together
}

/**
 * A number in the person's format: `1,234.56` · `1.234,56` · `1 234,56`.
 * `options` are Intl.NumberFormat digit options (minimumFractionDigits,
 * maximumFractionDigits, …); by default up to 3 decimals, like
 * `toLocaleString()`. Returns '' for a non-number.
 */
export function formatNumber(value, options = {}) {
  const n = typeof value === 'string' && value.trim() !== '' ? Number(value) : value
  if (typeof n !== 'number' || !Number.isFinite(n)) return ''
  const sep = NUMBER_SEPARATORS[formatPrefs.numberFormat]
  return new Intl.NumberFormat('en-US', options)
    .formatToParts(n)
    .map((p) => (p.type === 'group' ? sep.group : p.type === 'decimal' ? sep.decimal : p.value))
    .join('')
}

/** A money amount: the currency's own symbol + the number in the person's
 *  format, 2 decimals by default — e.g. "£48,500.00" / "£48.500,00". */
export function formatMoney(amount, currency, { decimals = 2 } = {}) {
  const n = typeof amount === 'string' && amount.trim() !== '' ? Number(amount) : amount
  if (typeof n !== 'number' || !Number.isFinite(n)) return ''
  const body = formatNumber(Math.abs(n), {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  })
  return `${n < 0 ? '-' : ''}${currencySymbol(currency)}${body}`
}

/**
 * Canonical combined date + time formatter.
 *
 * Output format: `DD/MM/YYYY HH:MM AM/PM`
 *   - Two-digit zero-padded day / month / year
 *   - Two-digit zero-padded 12-hour clock
 *   - Uppercase AM / PM
 *   - Single space between the date and the time
 *   - NO comma, NO seconds, NO "at"
 *
 * Example: `09/06/2026 02:17 PM`
 *
 * Accepts `Date | string (ISO) | number (epoch ms) | null | undefined`.
 * Returns an empty string for nullish / invalid input. Pure function —
 * never throws, no side effects.
 *
 * @param {Date|string|number|null|undefined} value
 * @returns {string}
 */
export function formatDateTime(value) {
  try {
    const dt = toDT(value)
    if (!dt) return ''
    return upperMeridiem(dt.toFormat(`${datePattern()} ${timePattern()}`))
  } catch {
    return ''
  }
}

/**
 * Canonical date-only formatter — used for the exceptions where the
 * time of day is meaningless (legal pages, daily-report bucket dates,
 * birthdays).
 *
 * Output format: `DD/MM/YYYY`
 *   - Two-digit zero-padded day / month / year (en-GB ordering)
 *
 * Example: `09/06/2026`
 *
 * Accepts `Date | string (ISO) | number (epoch ms) | null | undefined`.
 * Returns an empty string for nullish / invalid input. Pure function —
 * never throws, no side effects.
 *
 * @param {Date|string|number|null|undefined} value
 * @returns {string}
 */
export function formatDateOnly(value) {
  try {
    const dt = toDay(value)
    return dt ? dt.toFormat(datePattern()) : ''
  } catch {
    return ''
  }
}

/**
 * Calendar-day helpers for "overdue / due today / due tomorrow" logic.
 *
 * These compare CALENDAR DAYS in the active display timezone (the tenant's
 * zone) — NOT raw instants in the viewer's browser zone. A date-only due
 * date is stored at midnight, so a bare `new Date(due) < startOfDay(now)`
 * test silently shifts it across the UTC boundary and mis-files a "today"
 * task as overdue for anyone whose browser zone differs from where the
 * date was set. Bucketing by the display-zone day fixes that AND keeps the
 * classification consistent with what `formatDate`/`formatDateTime` render.
 *
 * Use `dayOffsetFromToday` for overdue/today/tomorrow buckets and
 * `dayKey`/`todayKey` for grouping or equality checks.
 */

/** Calendar day of `input` as a "yyyy-MM-dd" key in the display timezone.
 *  Null for missing / invalid input. */
export function dayKey(input) {
  const dt = toDay(input)
  return dt ? dt.toFormat('yyyy-MM-dd') : null
}

/** Today's calendar day as a "yyyy-MM-dd" key in the display timezone. */
export function todayKey() {
  return DateTime.now()
    .setZone(calendarZone() || undefined)
    .toFormat('yyyy-MM-dd')
}

/** Whole calendar days from TODAY to `input`'s day, in the display
 *  timezone: 0 = today, negative = past (overdue), 1 = tomorrow, … .
 *  Null for missing / invalid input. */
export function dayOffsetFromToday(input) {
  const dt = toDay(input)
  if (!dt) return null
  const today = DateTime.now()
    .setZone(calendarZone() || undefined)
    .startOf('day')
  return Math.round(dt.startOf('day').diff(today, 'days').days)
}
